import { Sprite, Loader, Point } from 'pixi.js';
import gsap from 'gsap';
import eventHub from '@/scripts/eventHub';
import { isMobile } from '@/scripts/utils';
import vars from '../vars';

class IntroStage {
  constructor(parent) {
    this.parent = parent;

    this.ref = new Sprite();
    this.sprite = new Sprite(Loader.shared.resources['images/INTRO_BACKGROUND'].texture);

    this.bushesLeftBack = new Sprite(Loader.shared.resources['images/INTRO_BUSHES_LEFT_BACK'].texture);
    this.bushesLeftFore = new Sprite(Loader.shared.resources['images/INTRO_BUSHES_LEFT_FORE'].texture);
    this.bushesRightBack = new Sprite(Loader.shared.resources['images/INTRO_BUSHES_RIGHT_BACK'].texture);
    this.bushesRightFore = new Sprite(Loader.shared.resources['images/INTRO_BUSHES_RIGHT_FORE'].texture);

    const backgroundSheet = Loader.shared.resources['images/BACKGROUND'].spritesheet.textures;
    this.branchesLeft = new Sprite(backgroundSheet['basic-branches-left.png']);
    this.branchesRight = new Sprite(backgroundSheet['basic-branches-right.png']);

    this.courtainBushLeftFore = new Sprite(Loader.shared.resources['images/COURTAIN_BUSH_LEFT'].texture);
    this.courtainBushLeftMid = new Sprite(Loader.shared.resources['images/COURTAIN_BUSH_LEFT'].texture);
    this.courtainBushLeftBack = new Sprite(Loader.shared.resources['images/COURTAIN_BUSH_LEFT'].texture);
    this.courtainBushRightFore = new Sprite(Loader.shared.resources['images/COURTAIN_BUSH_RIGHT'].texture);
    this.courtainBushRightMid = new Sprite(Loader.shared.resources['images/COURTAIN_BUSH_RIGHT'].texture);
    this.courtainBushRightBack = new Sprite(Loader.shared.resources['images/COURTAIN_BUSH_RIGHT'].texture);

    this.ref.addChild(this.sprite);
    this.ref.addChild(this.bushesLeftBack);
    this.ref.addChild(this.bushesRightBack);
    this.ref.addChild(this.bushesLeftFore);
    this.ref.addChild(this.bushesRightFore);

    this.ref.addChild(this.courtainBushLeftBack);
    this.ref.addChild(this.courtainBushRightBack);
    if (isMobile()) {
      this.ref.addChild(this.courtainBushLeftMid);
      this.ref.addChild(this.courtainBushRightMid);
    }
    this.ref.addChild(this.courtainBushLeftFore);
    this.ref.addChild(this.courtainBushRightFore);

    this.ref.addChild(this.branchesLeft);
    this.ref.addChild(this.branchesRight);

    this.resize();

    this.parent.addChild(this.ref);

    eventHub.$on('bush-in', () => this.bushIn());
  }

  resize() {
    this.ref.anchor = new Point(0.5, 0.5);

    this.sprite.width = vars.layout.FRAME_WIDTH;
    this.sprite.height = vars.layout.FRAME_HEIGHT;

    this.branchesLeft.anchor = new Point(0, 0);
    this.branchesLeft.position.x = 0 - 200;
    this.branchesLeft.position.y = 0;
    this.branchesRight.anchor = new Point(1, 0);
    this.branchesRight.position.x = this.sprite.width + 200;
    this.branchesRight.position.y = 0;

    this.bushesLeftBack.anchor = new Point(0, 1);
    this.bushesLeftBack.position.x = 0 - 100;
    this.bushesLeftBack.position.y = vars.layout.FRAME_HEIGHT;
    this.bushesLeftFore.anchor = new Point(0, 1);
    this.bushesLeftFore.position.x = 0 - 100;
    this.bushesLeftFore.position.y = vars.layout.FRAME_HEIGHT;

    this.bushesRightBack.anchor = new Point(1, 1);
    this.bushesRightBack.position.x = vars.layout.FRAME_WIDTH + 100;
    this.bushesRightBack.position.y = vars.layout.FRAME_HEIGHT;
    this.bushesRightFore.anchor = new Point(1, 1);
    this.bushesRightFore.position.x = vars.layout.FRAME_WIDTH + 100;
    this.bushesRightFore.position.y = vars.layout.FRAME_HEIGHT;

    this.courtainBushRightBack.anchor = new Point(1, 0);
    if (isMobile()) this.courtainBushRightMid.anchor = new Point(1, 0);
    this.courtainBushRightFore.anchor = new Point(1, 0);

    const unit = -85;
    this.courtainBushLeftFore.originalX = isMobile() ? -240 : -220;
    if (isMobile()) this.courtainBushLeftMid.originalX = -180;
    this.courtainBushLeftBack.originalX = unit;
    this.courtainBushRightFore.originalX = vars.layout.FRAME_WIDTH + (isMobile() ? 240 : 220);
    if (isMobile()) this.courtainBushRightMid.originalX = vars.layout.FRAME_WIDTH + 180;
    this.courtainBushRightBack.originalX = vars.layout.FRAME_WIDTH - unit;

    this.courtainBushLeftFore.position.x = -this.courtainBushLeftFore.width;
    if (isMobile()) this.courtainBushLeftMid.position.x = -this.courtainBushLeftFore.width;
    this.courtainBushLeftBack.position.x = -this.courtainBushRightBack.width;
    this.courtainBushRightFore.position.x = vars.layout.FRAME_WIDTH + (this.courtainBushRightFore.width);
    if (isMobile()) this.courtainBushRightMid.position.x = vars.layout.FRAME_WIDTH + (this.courtainBushRightFore.width);
    this.courtainBushRightBack.position.x = vars.layout.FRAME_WIDTH + (this.courtainBushRightBack.width);

    this.ref.position.y = 0;
  }

  firstReveal() {
    gsap.to(this.bushesLeftBack.position, {
      x: '-=300',
      ease: 'power2.inOut',
      duration: 1.6,
    });
    gsap.to(this.bushesLeftFore.position, {
      x: '-=300',
      ease: 'power2.inOut',
      duration: 1.6,
      delay: 0.1,
    });
    gsap.to(this.bushesRightBack.position, {
      x: '+=300',
      ease: 'power2.inOut',
      duration: 1.6,
    });
    gsap.to(this.bushesRightFore.position, {
      x: '+=300',
      ease: 'power2.inOut',
      duration: 1.6,
      delay: 0.1,
    });
  }

  midReveal() {
    const timeline = gsap.timeline({ defaults: { duration: 1.2, ease: 'power2.inOut' } });
    timeline.addLabel('start')
      .to(this.courtainBushLeftBack.position, {
        x: this.courtainBushLeftBack.originalX,
      }, 'start');
    if (isMobile()) {
      timeline.to(this.courtainBushLeftMid.position, {
        x: this.courtainBushLeftMid.originalX,
      }, 'start');
    }
    timeline.to(this.courtainBushLeftFore.position, {
      x: this.courtainBushLeftFore.originalX,
    }, 'start');
    timeline.to(this.courtainBushRightBack.position, {
      x: this.courtainBushRightBack.originalX,
    }, 'start');
    if (isMobile()) {
      timeline.to(this.courtainBushRightMid.position, {
        x: this.courtainBushRightMid.originalX,
      }, 'start');
    }
    timeline.to(this.courtainBushRightFore.position, {
      x: this.courtainBushRightFore.originalX,
    }, 'start');
    return timeline;
  }

  finalReveal(show = false) {
    const timeline = gsap.timeline({ defaults: { duration: 1.2, ease: 'power2.inOut' } });
    timeline.addLabel('start')
      .add(() => {
        this.sprite.renderable = show;
      })
      .to(this.courtainBushLeftBack.position, {
        x: -this.courtainBushLeftFore.width,
        delay: 0.2,
      }, 'start');
    if (isMobile()) {
      timeline.to(this.courtainBushLeftMid.position, {
        x: -this.courtainBushLeftFore.width,
      }, 'start');
    }
    timeline.to(this.courtainBushLeftFore.position, {
      x: -this.courtainBushLeftFore.width,
    }, 'start');
    timeline.to(this.courtainBushRightBack.position, {
      x: vars.layout.FRAME_WIDTH + (this.courtainBushRightFore.width),
      delay: 0.2,
    }, 'start');
    if (isMobile()) {
      timeline.to(this.courtainBushRightMid.position, {
        x: vars.layout.FRAME_WIDTH + (this.courtainBushRightFore.width),
        delay: 0.2,
      }, 'start');
    }
    timeline.to(this.courtainBushRightFore.position, {
      x: vars.layout.FRAME_WIDTH + (this.courtainBushRightFore.width),
    }, 'start');

    return timeline;
  }

  midRemove() {
    return this.midReveal();
  }

  finalRemove() {
    return this.finalReveal(true);
  }

  bushIn() {
    gsap.to(this.bushesLeftBack.position, {
      x: 0,
      ease: 'bloom',
      duration: 1,
    });
    gsap.to(this.bushesLeftFore.position, {
      x: 0,
      ease: 'bloom',
      duration: 1,
      delay: 0.1,
    });
    gsap.to(this.bushesRightBack.position, {
      x: vars.layout.FRAME_WIDTH,
      ease: 'bloom',
      duration: 1,
    });
    gsap.to(this.bushesRightFore.position, {
      x: vars.layout.FRAME_WIDTH,
      ease: 'bloom',
      duration: 1,
      delay: 0.1,
    });

    gsap.to(this.branchesLeft.position, {
      x: 0,
      ease: 'bloom',
      duration: 1,
      delay: 0.2,
    });
    gsap.to(this.branchesRight.position, {
      x: this.sprite.width,
      ease: 'bloom',
      duration: 1,
      delay: 0.2,
    });
  }
}

export default IntroStage;
